"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { getPlatformOverview } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AdminHeader } from "@/components/admin-header";

interface PlatformStats {
  total_merchants: number;
  active_merchants: number;
  total_customers: number;
  active_customers_24h: number;
  total_games_played: number;
  games_played_24h: number;
  qr_scans_total: number;
  qr_scans_24h: number;
  revenue_mtd: number;
  revenue_current_month: number;
}

export default function DashboardPage() {
  const router = useRouter();
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  async function loadStats() {
    setLoading(true);
    try {
      const data = await getPlatformOverview();
      setStats(data);
    } catch (error) {
      console.error("Failed to load stats:", error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <AdminHeader title="Dashboard" />
      <main className="flex-1 space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Platform Overview</h2>
            <p className="text-muted-foreground">
              Real-time metrics across all merchants
            </p>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Merchants</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-8 w-20 animate-pulse bg-muted rounded" />
              ) : (
                <div className="text-2xl font-bold">{stats?.total_merchants || 0}</div>
              )}
              <p className="text-xs text-muted-foreground">
                {stats?.active_merchants || 0} active
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Customers</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-8 w-20 animate-pulse bg-muted rounded" />
              ) : (
                <div className="text-2xl font-bold">{stats?.total_customers || 0}</div>
              )}
              <p className="text-xs text-muted-foreground">
                {stats?.active_customers_24h || 0} active (24h)
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Games Played</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-8 w-20 animate-pulse bg-muted rounded" />
              ) : (
                <div className="text-2xl font-bold">{stats?.total_games_played || 0}</div>
              )}
              <p className="text-xs text-muted-foreground">
                {stats?.games_played_24h || 0} today
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Revenue (MTD)</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-8 w-24 animate-pulse bg-muted rounded" />
              ) : (
                <div className="text-2xl font-bold">
                  ${(stats?.revenue_mtd || 0).toLocaleString()}
                </div>
              )}
              <p className="text-xs text-muted-foreground">Current month</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Engagement Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-4">
                <div className="flex items-center justify-between">
                  <dt className="text-sm font-medium text-muted-foreground">Total QR Scans</dt>
                  <dd className="text-2xl font-bold">
                    {loading ? (
                      <div className="h-8 w-20 animate-pulse bg-muted rounded" />
                    ) : (
                      stats?.qr_scans_total?.toLocaleString() || 0
                    )}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-sm font-medium text-muted-foreground">QR Scans (24h)</dt>
                  <dd className="text-2xl font-bold">
                    {loading ? (
                      <div className="h-8 w-20 animate-pulse bg-muted rounded" />
                    ) : (
                      stats?.qr_scans_24h?.toLocaleString() || 0
                    )}
                  </dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <button
                onClick={() => router.push("/merchants")}
                className="block w-full rounded-md border border-input bg-background px-4 py-2 text-sm text-left hover:bg-accent hover:text-accent-foreground"
              >
                View all merchants →
              </button>
              <button
                onClick={() => router.push("/customers")}
                className="block w-full rounded-md border border-input bg-background px-4 py-2 text-sm text-left hover:bg-accent hover:text-accent-foreground"
              >
                View all customers →
              </button>
              <button
                onClick={() => router.push("/billing")}
                className="block w-full rounded-md border border-input bg-background px-4 py-2 text-sm text-left hover:bg-accent hover:text-accent-foreground"
              >
                View billing overview →
              </button>
            </CardContent>
          </Card>
        </div>
      </main>
    </>
  );
}
